/**
 * Sounds: the library of files, and which of them each moment in the game plays.
 *
 * A file is brought in once and then named wherever it is wanted: a cue here,
 * a spell's cast or a character's death elsewhere. Bringing a file in again
 * under a name already in the library replaces it everywhere at once, which is
 * how a stand-in becomes the real thing.
 */

import { useRef, useState } from 'react';
import { useSoundLibrary } from './sound-library';
import { SOUND_CUES, SOUND_GROUPS, type SoundGroup } from '@inverse/common';
import type { SoundLibraryWire, SoundWire } from '@inverse/protocol';
import type { AdminCommand } from '../Admin';
import { Explain } from '../Explain';
import { sounds } from '../../game/sound';

export type SoundUpload = {
  name: string;
  title: string;
  contents: string;
  kind: 'wav' | 'ogg' | 'mp3';
};

/**
 * A sound from the library, with a way to hear it.
 *
 * For anywhere a sound is named: a spell, a character, an act. Empty is
 * whatever that thing plays when it has nothing of its own.
 */
export function SoundPicker({
  label,
  value,
  empty = 'None',
  onChange,
}: {
  label: string;
  value: string;
  empty?: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const library = useSoundLibrary();
  const known = library.sounds.some((one) => one.name === value);
  return (
    <label className="editor-field sound-picker">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{empty}</option>
        {!known && value ? <option value={value}>{value} (missing)</option> : null}
        {[...library.sounds]
          .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name))
          .map((one) => (
            <option key={one.name} value={one.name}>
              {one.title || one.name}
            </option>
          ))}
      </select>
      <button
        type="button"
        className="editor-tool"
        title="Hear it"
        disabled={!known}
        onClick={() => sounds.preview(value)}
      >
        ▶
      </button>
    </label>
  );
}

const KINDS = ['wav', 'ogg', 'mp3'] as const;

function kindOf(file: File): 'wav' | 'ogg' | 'mp3' | null {
  const ending = file.name.toLowerCase().split('.').pop() ?? '';
  return (KINDS as readonly string[]).includes(ending) ? (ending as 'wav' | 'ogg' | 'mp3') : null;
}

function nameFrom(file: string): string {
  return file
    .replace(/\.[a-z0-9]+$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

/** A binary file as the text the upload carries. */
async function asBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let at = 0; at < bytes.length; at += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  }
  return btoa(binary);
}

type Page = 'library' | 'cues';

/**
 * A slider that says what it is set to only once it is let go.
 *
 * Every step of a drag is not a save: the world writes the library down each
 * time it is told, and a drag is forty steps.
 */
function Range({
  value,
  max,
  step,
  onCommit,
}: {
  value: number;
  max: number;
  step: number;
  onCommit: (value: number) => void;
}): JSX.Element {
  // Starts again from whatever the world says each time that changes, which is
  // what the key on each one is for.
  const [held, setHeld] = useState<number | null>(null);
  const commit = (): void => {
    if (held !== null && held !== value) onCommit(held);
  };
  return (
    <input
      type="range"
      min={0}
      max={max}
      step={step}
      value={held ?? value}
      onChange={(event) => setHeld(Number(event.target.value))}
      onPointerUp={commit}
      onKeyUp={commit}
      onBlur={commit}
    />
  );
}

export function EditorSounds({
  onCommand,
  onUpload,
  spells,
  characters,
}: {
  onCommand: AdminCommand;
  /** Sends the file itself, which does not go over the game connection. */
  onUpload: (upload: SoundUpload) => Promise<string | null>;
  /** For saying what plays a sound before it is taken out. */
  spells: readonly { name: string; sounds: { cast: string; impact: string } }[];
  characters: readonly { id: number; name: string }[];
}): JSX.Element {
  const library = useSoundLibrary();
  const [page, setPage] = useState<Page>('library');
  const [chosen, setChosen] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [saying, setSaying] = useState('');
  const [busy, setBusy] = useState(false);
  const [warned, setWarned] = useState(false);
  const bringIn = useRef<HTMLInputElement>(null);
  const replace = useRef<HTMLInputElement>(null);

  const held = library.sounds.find((one) => one.name === chosen) ?? null;

  const usersOf = (name: string): string[] => {
    const users: string[] = [];
    for (const cue of library.cues) {
      if (cue.sounds.includes(name)) {
        users.push(SOUND_CUES.find((one) => one.cue === cue.cue)?.label ?? cue.cue);
      }
    }
    for (const spell of spells) {
      if (spell.sounds.cast === name || spell.sounds.impact === name) users.push(spell.name);
    }
    for (const npc of library.npcs) {
      if (npc.attack === name || npc.hurt === name || npc.die === name) {
        users.push(characters.find((one) => one.id === npc.id)?.name ?? `character ${npc.id}`);
      }
    }
    return users;
  };

  const send = async (file: File, name: string, title: string): Promise<void> => {
    const kind = kindOf(file);
    if (!kind) {
      setSaying('A sound has to be a .wav, .ogg or .mp3 file.');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setSaying('That file is too big. Keep sounds under six megabytes.');
      return;
    }
    setBusy(true);
    setSaying('');
    try {
      const problem = await onUpload({ name, title, contents: await asBase64(file), kind });
      setSaying(problem ?? '');
      if (!problem) setChosen(name);
    } catch (error) {
      setSaying(error instanceof Error ? error.message : 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const change = (patch: Partial<SoundWire>): void => {
    if (!held) return;
    const next = { ...held, ...patch };
    onCommand(
      'saveSound',
      { volume: next.volume, vary: next.vary },
      { sound: next.name, title: next.title },
    );
  };

  const shown = library.sounds
    .filter((one) => {
      const wanted = search.trim().toLowerCase();
      return !wanted || one.name.includes(wanted) || one.title.toLowerCase().includes(wanted);
    })
    .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name));

  const users = held ? usersOf(held.name) : [];

  return (
    <>
      <div className="editor-brushes">
        <button
          type="button"
          className={page === 'library' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => setPage('library')}
        >
          Library
        </button>
        <button
          type="button"
          className={page === 'cues' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => setPage('cues')}
        >
          Where They Play
        </button>
      </div>

      {page === 'library' ? (
        <>
          <input
            ref={bringIn}
            className="editor-file"
            type="file"
            accept=".wav,.ogg,.mp3,audio/wav,audio/ogg,audio/mpeg"
            onChange={() => {
              const picked = bringIn.current?.files?.[0];
              if (picked)
                void send(picked, nameFrom(picked.name), picked.name.replace(/\.[a-z0-9]+$/i, ''));
              if (bringIn.current) bringIn.current.value = '';
            }}
          />
          <p className="hint">
            A .wav, .ogg or .mp3 file.
            <Explain>
              Named after the file. One brought in under a name that is already here replaces that
              one wherever it plays.
            </Explain>
          </p>
          {busy ? <p className="hint">Sending it over.</p> : null}
          {saying ? <p className="hint warning">{saying}</p> : null}

          <input
            type="text"
            className="editor-search"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="editor-list sound-list">
            {shown.map((one) => (
              <div
                key={one.name}
                className={one.name === chosen ? 'editor-row on sound-row' : 'editor-row sound-row'}
              >
                <button
                  type="button"
                  className="sound-play"
                  title="Hear it"
                  onClick={() => sounds.preview(one.name)}
                >
                  ▶
                </button>
                <button
                  type="button"
                  className="sound-name"
                  onClick={() => {
                    setChosen(one.name);
                    setWarned(false);
                  }}
                >
                  <span>{one.title || one.name}</span>
                  <span className="editor-row-id">{one.name}</span>
                </button>
              </div>
            ))}
          </div>
          {library.sounds.length === 0 ? <p className="hint">Nothing brought in yet.</p> : null}

          {held ? (
            <div className="editor-facts">
              <h3>{held.title || held.name}</h3>
              <label className="editor-field">
                <span>Called</span>
                <input
                  type="text"
                  maxLength={64}
                  defaultValue={held.title}
                  key={held.name}
                  onBlur={(event) => {
                    if (event.target.value !== held.title) change({ title: event.target.value });
                  }}
                />
              </label>
              <label className="editor-field">
                <span>Loudness</span>
                <Range
                  key={held.volume}
                  value={held.volume}
                  max={2}
                  step={0.05}
                  onCommit={(volume) => change({ volume })}
                />
                <span className="editor-row-id">{Math.round(held.volume * 100)}%</span>
              </label>
              <label className="editor-field">
                <span>Pitch wanders</span>
                <Range
                  key={held.vary}
                  value={held.vary}
                  max={0.5}
                  step={0.01}
                  onCommit={(vary) => change({ vary })}
                />
                <span className="editor-row-id">{Math.round(held.vary * 100)}%</span>
              </label>
              <p className="hint">
                A little wander keeps something heard often, like a footstep, from sounding the same
                every time.
              </p>

              <div className="editor-brushes">
                <button
                  type="button"
                  className="editor-tab"
                  onClick={() => sounds.preview(held.name)}
                >
                  ▶ Hear It
                </button>
                <button
                  type="button"
                  className="editor-tab"
                  onClick={() => replace.current?.click()}
                >
                  Replace With A File
                </button>
              </div>
              <input
                ref={replace}
                type="file"
                hidden
                accept=".wav,.ogg,.mp3,audio/wav,audio/ogg,audio/mpeg"
                onChange={() => {
                  const picked = replace.current?.files?.[0];
                  if (picked) void send(picked, held.name, held.title);
                  if (replace.current) replace.current.value = '';
                }}
              />

              <h4>Played By</h4>
              <p className="hint">
                {users.length === 0
                  ? 'Nothing plays this yet.'
                  : `${users.slice(0, 6).join(', ')}${users.length > 6 ? ` and ${users.length - 6} more` : ''}.`}
              </p>

              <button
                type="button"
                className="wide danger"
                onClick={() => {
                  onCommand(
                    'removeSound',
                    { anyway: warned || users.length === 0 ? 1 : 0 },
                    { sound: held.name },
                  );
                  if (users.length > 0 && !warned) {
                    setWarned(true);
                    return;
                  }
                  setChosen(null);
                  setWarned(false);
                }}
              >
                {users.length > 0 && warned ? 'Take It Out Anyway' : 'Take It Out'}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <CuePage library={library} onCommand={onCommand} />
      )}
    </>
  );
}

/** Every moment the game has, by group, with the sounds each one picks from. */
function CuePage({
  library,
  onCommand,
}: {
  library: SoundLibraryWire;
  onCommand: AdminCommand;
}): JSX.Element {
  const [group, setGroup] = useState<SoundGroup>('steps');
  const save = (cue: string, list: readonly string[], volume: number): void =>
    onCommand('saveCue', { volume }, { cue, sounds: list.join(',') });
  const titleOf = (name: string): string =>
    library.sounds.find((one) => one.name === name)?.title || name;

  return (
    <>
      <p className="hint">
        Each moment plays one of its sounds, picked at random.
        <Explain>
          Give a moment more than one sound and it will not sound the same twice running.
        </Explain>
      </p>
      <div className="editor-brushes">
        {SOUND_GROUPS.map((one) => (
          <button
            key={one.group}
            type="button"
            className={group === one.group ? 'editor-tab on' : 'editor-tab'}
            onClick={() => setGroup(one.group)}
          >
            {one.label}
          </button>
        ))}
      </div>

      {SOUND_CUES.filter((one) => one.group === group).map((one) => {
        const held = library.cues.find((entry) => entry.cue === one.cue);
        const list = held?.sounds ?? [];
        const volume = held?.volume ?? 1;
        return (
          <div key={one.cue} className="magic-box sound-cue">
            <div className="sound-cue-head">
              <strong>{one.label}</strong>
              <button
                type="button"
                className="editor-tool"
                title="Hear it as the game plays it"
                disabled={list.length === 0}
                onClick={() => sounds.previewCue(one.cue)}
              >
                ▶
              </button>
            </div>
            <div className="sound-chips">
              {list.length === 0 ? <span className="hint">Silent.</span> : null}
              {list.map((name) => (
                <span key={name} className="sound-chip">
                  {titleOf(name)}
                  <button
                    type="button"
                    title="Take it off this"
                    onClick={() =>
                      save(
                        one.cue,
                        list.filter((entry) => entry !== name),
                        volume,
                      )
                    }
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <div className="editor-brushes">
              <select
                value=""
                onChange={(event) => {
                  if (event.target.value) save(one.cue, [...list, event.target.value], volume);
                }}
              >
                <option value="">Add a sound</option>
                {[...library.sounds]
                  .filter((entry) => !list.includes(entry.name))
                  .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name))
                  .map((entry) => (
                    <option key={entry.name} value={entry.name}>
                      {entry.title || entry.name}
                    </option>
                  ))}
              </select>
              <label className="editor-field">
                <span>Loudness</span>
                <Range
                  key={volume}
                  value={volume}
                  max={2}
                  step={0.05}
                  onCommit={(next) => save(one.cue, list, next)}
                />
              </label>
            </div>
            {one.loops ? <p className="hint">Played round and round while it applies.</p> : null}
          </div>
        );
      })}
    </>
  );
}
